import React, { useEffect, useMemo, useState } from "react";
import { useAuthBootstrap } from "../../context/AuthBootstrapContext";
import "./index.css";
import { Image as ImageIcon } from "lucide-react";
import TypicalAvailabilityPicker from "./TypicalAvailabilityPicker";
import cupIconSrc from "../../assets/cupIconSrc";

const API_BASE =
  process.env.REACT_APP_API_BASE || "https://api-2026.fikacreatives.co";

const FEATURED_EXAMPLES = [
  "https://vdbpbpjwtbcyhnnwynox.supabase.co/storage/v1/object/sign/landing%20page/memberships/Sonika.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9kZGI0MGU3NC04Nzk2LTRhZmItOTViNy0wNDQ5YzAyNWMzZjciLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJsYW5kaW5nIHBhZ2UvbWVtYmVyc2hpcHMvU29uaWthLnBuZyIsImlhdCI6MTc3OTYzOTE5NywiZXhwIjoxODExMTc1MTk3fQ.tRgmW7TqoOOtpuo_PF3Lk06A7KHMrXtYDjsRF3VnXKs",
  "https://vdbpbpjwtbcyhnnwynox.supabase.co/storage/v1/object/sign/landing%20page/memberships/Vrinda.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9kZGI0MGU3NC04Nzk2LTRhZmItOTViNy0wNDQ5YzAyNWMzZjciLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJsYW5kaW5nIHBhZ2UvbWVtYmVyc2hpcHMvVnJpbmRhLnBuZyIsImlhdCI6MTc3OTYzOTIxNywiZXhwIjoxODExMTc1MjE3fQ.jo46YyNyeCbTJpCTKm7N8VP3byeUXeP3wejTJhjgxII",
  "https://vdbpbpjwtbcyhnnwynox.supabase.co/storage/v1/object/sign/landing%20page/memberships/Isaac.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9kZGI0MGU3NC04Nzk2LTRhZmItOTViNy0wNDQ5YzAyNWMzZjciLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJsYW5kaW5nIHBhZ2UvbWVtYmVyc2hpcHMvSXNhYWMucG5nIiwiaWF0IjoxNzc5NjM5NjE1LCJleHAiOjE4MTExNzU2MTV9.CCz_Tchy4dtH3d36vva9peOu6-eUuxhuDSlnJMEcDWc",
];

const MOTIVATION_OPTIONS = [
  { emoji: "👀", label: "Actively looking for…" },
  { emoji: "🎪", label: "Hoping to get better at…" },
  { emoji: "🎨", label: "Want to learn how to…" },
  { emoji: "💼", label: "Hiring" },
  { emoji: "📓", label: "Looking for gigs" },
  { emoji: "💡", label: "Bring a dream idea to life" },
];

const CYCLE_SLOT_OPTIONS = [
  { value: "1", emoji: "1️⃣", day: "Wed", time: "6 PM", wkKey: "wed" },
  { value: "2", emoji: "2️⃣", day: "Fri", time: "4 PM", wkKey: "fri" },
  { value: "3", emoji: "3️⃣", day: "Sun", time: "1 PM", wkKey: "sun" },
  { value: "4", emoji: "4️⃣", day: "Can't make it", time: null, wkKey: null },
];

function getFollowingWeekDates(cycleDateStr) {
  if (!cycleDateStr) return null;
  const d = new Date(cycleDateStr + "T12:00:00");
  const dayOfWeek = d.getDay();
  const daysToNextMonday = dayOfWeek === 0 ? 1 : 8 - dayOfWeek;
  const mon = new Date(d);
  mon.setDate(d.getDate() + daysToNextMonday);
  const fmt = (offset) => {
    const x = new Date(mon);
    x.setDate(mon.getDate() + offset);
    return x.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };
  return { wed: fmt(2), fri: fmt(4), sun: fmt(6) };
}

// ── Utilities ────────────────────────────────────────────────

async function readJsonSafely(res) {
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) return res.json();
  const text = await res.text();
  throw new Error(text || "Unexpected server response");
}

async function patchOnboardingJson(payload) {
  const res = await fetch(`${API_BASE}/api/onboarding`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await readJsonSafely(res);
  if (!res.ok || !data?.ok) throw new Error(data?.error || "Failed to save onboarding");
  return data;
}

async function createSignedUpload(kind, file) {
  const res = await fetch(`${API_BASE}/api/onboarding`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      action: "create_upload_url",
      file_kind: kind,
      file_name: file?.name || null,
      content_type: file?.type || "application/octet-stream",
    }),
  });
  const data = await readJsonSafely(res);
  if (!res.ok || !data?.ok) throw new Error(data?.error || `Could not prepare ${kind} upload`);
  return data;
}

async function uploadFileDirectToStorage(kind, file) {
  if (!file) return null;
  const prep = await createSignedUpload(kind, file);
  const uploadRes = await fetch(prep.signedUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
  if (!uploadRes.ok) {
    const text = await uploadRes.text();
    throw new Error(text || `Upload failed for ${kind}`);
  }
  return { storagePath: prep.storagePath, publicUrl: prep.publicUrl };
}

function normalizeZip(value) {
  return String(value || "").replace(/\D/g, "").slice(0, 5);
}

function formatFileSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Main component ───────────────────────────────────────────

export default function OnboardingFlow() {
  const { person, profileAssets, setProfileAssets, refresh } = useAuthBootstrap();

  const missingZip = !String(person?.zip_code || "").trim();
  const name = person?.first_name || "there";

  const STEP_DEFS = useMemo(() => {
    const steps = [
      {
        key: "welcome",
        heading: `Welcome to Fika, ${name}!`,
        subtitle:
          "We’re excited to have you as part of the creative community, and can’t wait for you to meet talented creatives near you over Fikas.",
        isIntro: true,
      },
    ];
    if (missingZip) {
      steps.push({
        key: "basics",
        heading: "The basics",
        subtitle: "We use your ZIP code to personalize your Fika matches.",
      });
    }
    steps.push(
      {
        key: "availability",
        heading: "When would you like to Fika?",
        subtitle:
          "1:1 Fikas: This is a recurring schedule, so we recommend choosing times you’re typically available throughout the week. You may already work from cafés or take creative meetings there — think about when it would feel natural to step away, recharge, and “fika” as part of your weekly or weekend routine.",
      },
      {
        key: "motivations",
        heading: "Why are you excited to Fika?",
        subtitle: "Setting your intention makes it easier to find your people.",
      },
      {
        key: "profilePhoto",
        heading: "Pick your profile photo",
        subtitle:
          "Choose an image that will help members recognize you when you meet for coffee.",
      },
      {
        key: "favoriteWork",
        heading: "What's a project you're proud of?",
        subtitle:
          "High-res, well-lit images and gifs typically get the most visits.\n*For writers, we recommend uploading a screenshot or photo of a writing sample.",
      },
      {
        key: "coffeeFrequency",
        heading: "How many times would you like to have coffee with a new creative?",
        subtitle: "We'll use this information to provide a valuable experience",
      },
      {
        key: "cycleAvailability",
        heading: "When are you free to be matched with creatives?",
        subtitle: "Group Fikas: Select all that apply. You'll get an invitation to a WhatsApp group with your creative matches, and a time and place to connect.",
      },
      {
        key: "featuredConsent",
        heading: "Be discovered by the community",
        subtitle:
          "When featured, members can find your profile and send you a coffee invite based on your work — even before you're matched.",
      },
      {
        key: "allSet",
        heading: "You're all set",
        subtitle:
          "Your profile is ready to publish. Once you submit, you can explore other members and start sending and receiving coffee invites. If you haven't already responded with your availability, expect to receive a text within two weeks, replying with your availability to meet creatives over coffee. RSVP to upcoming coworking and happy hour sessions!",
      }
    );
    return steps;
  }, [missingZip, name]);

  const [stepIndex, setStepIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Per-step state
  const [zipCode, setZipCode] = useState(String(person?.zip_code || ""));
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [projectImageFile, setProjectImageFile] = useState(null);
  const [profilePreviewUrl, setProfilePreviewUrl] = useState("");
  const [projectPreviewUrl, setProjectPreviewUrl] = useState("");
  const [selectedMotivations, setSelectedMotivations] = useState(new Set());
  const [motivationDetail, setMotivationDetail] = useState("");
  const [availabilitySlots, setAvailabilitySlots] = useState(new Set());
  const [projectLink, setProjectLink] = useState(
    profileAssets?.project_link || ""
  );
  // const [projectTab, setProjectTab] = useState("image"); // "image" | "video" — commented out with studio tab
  const [allowFeaturedProfile, setAllowFeaturedProfile] = useState(false);
  const [coffeeFrequency, setCoffeeFrequency] = useState("1x_week");
  const [cycleAvailSelected, setCycleAvailSelected] = useState(new Set());
  const [nextCycleId, setNextCycleId] = useState(null);
  const [nextCycleDate, setNextCycleDate] = useState(null);

  const currentStepDef = STEP_DEFS[stepIndex];

  useEffect(() => {
    if (!person?.id) return;
    fetch(`${API_BASE}/api/fikas?personId=${person.id}`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => {
        if (d.nextCycleId)   setNextCycleId(d.nextCycleId);
        if (d.nextCycleDate) setNextCycleDate(d.nextCycleDate);
      })
      .catch(() => {});
  }, [person?.id]);

  useEffect(() => {
    if (!profileImageFile) { setProfilePreviewUrl(""); return; }
    const url = URL.createObjectURL(profileImageFile);
    setProfilePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [profileImageFile]);

  useEffect(() => {
    if (!projectImageFile) { setProjectPreviewUrl(""); return; }
    const url = URL.createObjectURL(projectImageFile);
    setProjectPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [projectImageFile]);

  // ── Motivation helpers ──────────────────────────────────────

  function toggleMotivation(label) {
    if (label === "Not sure yet") {
      setSelectedMotivations(new Set(["Not sure yet"]));
      setMotivationDetail("");
      return;
    }
    setSelectedMotivations((prev) => {
      const next = new Set(prev);
      next.delete("Not sure yet");
      next.has(label) ? next.delete(label) : next.add(label);
      return next;
    });
  }

  const detailLabel = useMemo(() => {
    return Array.from(selectedMotivations).find((m) => m.endsWith("…")) || null;
  }, [selectedMotivations]);

  // ── Navigation ──────────────────────────────────────────────

  function prevStep() {
    setError("");
    if (stepIndex > 0) setStepIndex((p) => p - 1);
  }

  function nextSimpleStep() {
    setError("");
    setStepIndex((p) => p + 1);
  }

  async function handleNext() {
    const { key } = currentStepDef;
    setError("");
    if (key === "welcome") return nextSimpleStep();
    if (key === "basics") return saveBasicsAndNext();
    if (key === "availability") return saveAvailabilityAndNext();
    if (key === "motivations") return saveMotivationsAndNext();
    if (key === "profilePhoto") {
      if (!profileImageFile && !profileAssets?.profile_image_url) {
        setError("Please upload a profile photo to continue.");
        return;
      }
      return saveProfileImageAndNext();
    }
    if (key === "favoriteWork") {
      if (!projectImageFile && !profileAssets?.project_image_url) {
        setError("Please upload a project image to continue.");
        return;
      }
      return saveFavoriteWorkAndNext();
    }
    if (key === "coffeeFrequency") return saveCoffeeFrequencyAndNext();
    if (key === "cycleAvailability") return saveCycleAvailabilityAndNext();
    if (key === "featuredConsent") return saveFeaturedConsentAndNext();
    if (key === "allSet") return completeOnboarding();
    nextSimpleStep();
  }

  // ── Save functions ──────────────────────────────────────────

  async function saveBasicsAndNext() {
    setSaving(true);
    setError("");
    try {
      const normalized = normalizeZip(zipCode);
      if (!/^\d{5}$/.test(normalized)) throw new Error("Enter a valid 5-digit ZIP code.");
      const data = await patchOnboardingJson({ action: "save_step", zip_code: normalized });
      setProfileAssets(data.profileAssets);
      setZipCode(normalized);
      nextSimpleStep();
    } catch (err) {
      setError(err.message || "Could not save ZIP code.");
    } finally {
      setSaving(false);
    }
  }

  async function saveAvailabilityAndNext() {
    if (availabilitySlots.size > 0) {
      setSaving(true);
      try {
        await fetch(`${API_BASE}/api/settings/typical-availability`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ slots: Array.from(availabilitySlots) }),
        });
      } catch (e) {
        console.error("typical-availability save failed:", e);
      } finally {
        setSaving(false);
      }
    }
    nextSimpleStep();
  }

  async function saveMotivationsAndNext() {
    setSaving(true);
    try {
      const data = await patchOnboardingJson({
        action: "save_step",
        motivations: {
          tags: Array.from(selectedMotivations),
          detail: motivationDetail.trim() || null,
        },
      });
      setProfileAssets(data.profileAssets);
    } catch (err) {
      console.error("motivations save failed:", err);
    } finally {
      setSaving(false);
    }
    nextSimpleStep();
  }

  async function saveProfileImageAndNext() {
    setSaving(true);
    setError("");
    try {
      let uploaded = null;
      if (profileImageFile) {
        uploaded = await uploadFileDirectToStorage("profile", profileImageFile);
      }
      const data = await patchOnboardingJson({
        action: "save_step",
        profile_image_url: uploaded?.publicUrl || null,
      });
      setProfileAssets(data.profileAssets);
      nextSimpleStep();
    } catch (err) {
      setError(err.message || "Could not save profile image.");
    } finally {
      setSaving(false);
    }
  }

  async function saveCoffeeFrequencyAndNext() {
    setSaving(true);
    try {
      await patchOnboardingJson({ action: "save_step", coffee_frequency: coffeeFrequency });
    } catch (err) {
      console.error("coffee frequency save failed:", err);
    } finally {
      setSaving(false);
    }
    nextSimpleStep();
  }

  async function saveCycleAvailabilityAndNext() {
    if (nextCycleId && cycleAvailSelected.size > 0) {
      setSaving(true);
      try {
        await fetch(`${API_BASE}/api/fikas/respond`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ cycleId: nextCycleId, slots: [...cycleAvailSelected] }),
        });
      } catch (err) {
        console.error("cycle availability save failed:", err);
      } finally {
        setSaving(false);
      }
    }
    nextSimpleStep();
  }

  async function saveFeaturedConsentAndNext() {
    setSaving(true);
    try {
      const data = await patchOnboardingJson({
        action: "save_step",
        allow_featured_profile: allowFeaturedProfile,
      });
      setProfileAssets(data.profileAssets);
    } catch (err) {
      console.error("featured consent save failed:", err);
    } finally {
      setSaving(false);
    }
    nextSimpleStep();
  }

  async function saveFavoriteWorkAndNext() {
    setSaving(true);
    setError("");
    try {
      let uploaded = null;
      if (projectImageFile) {
        uploaded = await uploadFileDirectToStorage("project", projectImageFile);
      }
      const data = await patchOnboardingJson({
        action: "save_step",
        project_image_url: uploaded?.publicUrl || null,
      });
      setProfileAssets(data.profileAssets);
      nextSimpleStep();
    } catch (err) {
      setError(err.message || "Could not save favorite work.");
    } finally {
      setSaving(false);
    }
  }

  async function completeOnboarding() {
    setSaving(true);
    setError("");
    try {
      const data = await patchOnboardingJson({ action: "complete" });
      setProfileAssets({ ...data.profileAssets, onboarding_completed: true });
      await refresh();
    } catch (err) {
      setError(err.message || "Could not complete onboarding.");
    } finally {
      setSaving(false);
    }
  }

  // ── Image upload helper ─────────────────────────────────────

  function renderImageUploadBox({ file, previewUrl, onChange, circular = false }) {
    return (
      <label className={`onb-upload-box ${circular ? "onb-upload-box--circle" : ""}`}>
        <input
          type="file"
          accept="image/*"
          className="ftueHiddenInput"
          onChange={(e) => {
            const next = e.target.files?.[0] || null;
            setError("");
            if (next && !next.type?.startsWith("image/")) {
              setError("Please upload an image file.");
              return;
            }
            onChange(next);
          }}
        />
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Preview"
            className={`onb-upload-preview ${circular ? "onb-upload-preview--circle" : ""}`}
          />
        ) : (
          <div className="onb-upload-empty">
            <ImageIcon size={28} color="#aaa" />
            <span className="onb-upload-hint">
              Ensure the image fits a 1:1 ratio, is below 10 MB, and is in JPG, PNG, GIF, or JPEG format.
            </span>
            <span className="onb-upload-btn">Upload</span>
          </div>
        )}
        {file && (
          <div className="onb-upload-filename">
            {file.name} · {formatFileSize(file.size)}
          </div>
        )}
      </label>
    );
  }

  // ── Step right-panel content ────────────────────────────────

  function renderRightContent() {
    switch (currentStepDef.key) {
      case "basics":
        return (
          <div className="onb-field">
            <label className="onb-label">ZIP code</label>
            <input
              className="onb-input"
              inputMode="numeric"
              maxLength={5}
              value={zipCode}
              onChange={(e) => setZipCode(normalizeZip(e.target.value))}
              placeholder="Enter your 5-digit ZIP code"
            />
            <p className="onb-helper">
              We use this to personalize your experience and show relevant events.
            </p>
          </div>
        );

      case "availability":
        return (
          <TypicalAvailabilityPicker
            hideButton
            onSlotsChange={setAvailabilitySlots}
          />
        );

      case "motivations":
        return (
          <div className="onb-motivations">
            <div className="onb-motivation-grid">
              {MOTIVATION_OPTIONS.map(({ emoji, label }) => (
                <button
                  key={label}
                  type="button"
                  className={`onb-motivation-chip${selectedMotivations.has(label) ? " onb-motivation-chip--active" : ""}`}
                  onClick={() => toggleMotivation(label)}
                >
                  <span>{emoji}</span>
                  <span>{label}</span>
                </button>
              ))}
            </div>
            <button
              type="button"
              className={`onb-not-sure${selectedMotivations.has("Not sure yet") ? " onb-not-sure--active" : ""}`}
              onClick={() => toggleMotivation("Not sure yet")}
            >
              Not sure yet
            </button>
            {detailLabel && (
              <div className="onb-field" style={{ marginTop: 20 }}>
                <label className="onb-label">{detailLabel}</label>
                <textarea
                  className="onb-textarea"
                  rows={4}
                  placeholder="Share more about what you're looking for…"
                  value={motivationDetail}
                  onChange={(e) => setMotivationDetail(e.target.value)}
                />
              </div>
            )}
          </div>
        );

      case "profilePhoto":
        return renderImageUploadBox({
          file: profileImageFile,
          previewUrl: profilePreviewUrl,
          onChange: setProfileImageFile,
          circular: true,
        });

      case "coffeeFrequency":
        return (
          <div className="onb-freq-options">
            {[
              { value: "1x_week",    cups: 1, label: "1x / week" },
              { value: "2x_month",   cups: 2, label: "2x / month" },
              { value: "occasional", cups: 3, label: "Occasional" },
            ].map(({ value, cups, label }) => {
              const active = coffeeFrequency === value;
              return (
                <button
                  key={value}
                  type="button"
                  className={`onb-freq-card${active ? " onb-freq-card--active" : ""}`}
                  onClick={() => setCoffeeFrequency(value)}
                >
                  <div className="onb-freq-cups">
                    {Array.from({ length: cups }).map((_, i) => (
                      <img key={i} src={cupIconSrc} alt="" style={{ width: 34, height: 34, objectFit: "contain" }} />
                    ))}
                  </div>
                  <div className="onb-freq-label">{label}</div>
                </button>
              );
            })}
          </div>
        );

      case "cycleAvailability": {
        const wk = getFollowingWeekDates(nextCycleDate);
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%" }}>
            {CYCLE_SLOT_OPTIONS.map(({ value, emoji, day, time, wkKey }) => {
              const isActive = cycleAvailSelected.has(value);
              const date = wk?.[wkKey] ?? null;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setCycleAvailSelected(prev => {
                    const next = new Set(prev);
                    if (value === "4") {
                      return next.has("4") ? new Set() : new Set(["4"]);
                    }
                    next.delete("4");
                    next.has(value) ? next.delete(value) : next.add(value);
                    return next;
                  })}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    padding: "14px 18px",
                    border: isActive ? "2px solid #1a1a2e" : "1.5px solid #e0d9d0",
                    borderRadius: 14,
                    background: isActive ? "#1a1a2e" : "#faf9f7",
                    color: isActive ? "#fff" : "#1a1a2e",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    textAlign: "left",
                    transition: "all 0.15s ease",
                    width: "100%",
                  }}
                >
                  <span style={{ fontSize: 22 }}>{emoji}</span>
                  <div>
                    {wkKey ? (
                      <>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>Next {day}{date ? `, ${date}` : ""}</div>
                        <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>{time}</div>
                      </>
                    ) : (
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{day}</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        );
      }

      case "featuredConsent":
        return (
          <div className="onb-featured">
            <div
              className="onb-toggle-row"
              onClick={() => setAllowFeaturedProfile((p) => !p)}
              role="switch"
              aria-checked={allowFeaturedProfile}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === " " || e.key === "Enter") {
                  e.preventDefault();
                  setAllowFeaturedProfile((p) => !p);
                }
              }}
            >
              <div className="onb-toggle-label">
                <span className="onb-toggle-title">Show me in the community</span>
                <span className="onb-toggle-sub">
                  Let members discover your profile and send you a coffee invite based on your work, outside of your biweekly matches. You're allowed 1 to send/month. Once you send the coffee invite, your phone number will be shown to coordinate meeting up, if you consent to it.
                </span>
              </div>
              <div className={`onb-toggle${allowFeaturedProfile ? " onb-toggle--on" : ""}`}>
                <div className="onb-toggle-thumb" />
              </div>
            </div>

            <p className="onb-featured-preview-label">What the community looks like</p>
            <div className="onb-featured-preview-row">
              {FEATURED_EXAMPLES.map((url, i) => (
                <div key={i} className="onb-featured-preview-card">
                  <img src={url} alt="" className="onb-featured-preview-img" />
                </div>
              ))}
            </div>
          </div>
        );

      case "favoriteWork":
        return (
          <div>
            {/* Tab row commented out until video/studio tab is ready
            <div className="onb-tab-row">
              <button
                type="button"
                className={`onb-tab${projectTab === "image" ? " onb-tab--active" : ""}`}
                onClick={() => setProjectTab("image")}
              >
                <span style={{ marginRight: 6 }}>🖼</span> Image / Gif
              </button>
              <button
                type="button"
                className={`onb-tab${projectTab === "video" ? " onb-tab--active" : ""}`}
                onClick={() => setProjectTab("video")}
              >
                <span style={{ marginRight: 6 }}>📹</span> Video
              </button>
            </div>
            */}

            {renderImageUploadBox({
              file: projectImageFile,
              previewUrl: projectPreviewUrl,
              onChange: setProjectImageFile,
              circular: false,
            })}
            <div className="onb-field" style={{ marginTop: 20 }}>
              <label className="onb-label">
                Add a project link <span className="onb-optional">(optional)</span>
              </label>
              <p className="onb-helper" style={{ marginTop: 0, marginBottom: 8 }}>
                Link to more details, video, websites, apps, or any other creative medium
              </p>
              <input
                className="onb-input"
                type="url"
                placeholder="https://"
                value={projectLink}
                onChange={(e) => setProjectLink(e.target.value)}
              />
            </div>
          </div>
        );

      case "allSet":
        return (
          <div className="onb-allset">
            <img
              src="https://vdbpbpjwtbcyhnnwynox.supabase.co/storage/v1/object/public/profile-assets/All%20pictures.png"
              alt="Welcome to Fika"
              className="onb-allset-img"
            />
          </div>
        );

      default:
        return null;
    }
  }

  // ── Render ──────────────────────────────────────────────────

  const isLast = currentStepDef.key === "allSet";

  // Intro steps (welcome) don't count toward the progress bar
  const progressSteps = STEP_DEFS.filter((s) => !s.isIntro);
  const progressIndex = STEP_DEFS.slice(0, stepIndex + 1).filter((s) => !s.isIntro).length - 1;

  // ── Welcome screen: same width as two-column modal ───────
  if (currentStepDef.key === "welcome") {
    return (
      <div className="ftueOverlay">
        <div className="ftueBackdrop" />
        <div className="ftueShell">
          <div className="onb-modal onb-modal--welcome">
            <div style={{ marginBottom: 20 }}>
              <img
                src="https://vdbpbpjwtbcyhnnwynox.supabase.co/storage/v1/object/sign/landing%20page/blue%20fika%20logo.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9kZGI0MGU3NC04Nzk2LTRhZmItOTViNy0wNDQ5YzAyNWMzZjciLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJsYW5kaW5nIHBhZ2UvYmx1ZSBmaWthIGxvZ28ucG5nIiwiaWF0IjoxNzc2NjYyMzM5LCJleHAiOjE4MDgxOTgzMzl9.zdOS9FZFMwJlRDy-2LaXo5n7FjgGcxmpUtliWGCSgWI"
                alt="Fika logo"
                style={{ width: 72 }}
              />
            </div>

            <div className="onb-welcome-video-wrap">
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster="https://vdbpbpjwtbcyhnnwynox.supabase.co/storage/v1/object/public/profile-assets/All%20pictures.png"
                className="onb-welcome-video"
              >
                <source
                  src="https://vdbpbpjwtbcyhnnwynox.supabase.co/storage/v1/object/public/profile-assets/7F21FCC2-E2A1-4AE2-B8B8-BF886F229E8B%20(1)%20(1).mp4"
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            </div>

            <h1 className="onb-welcome-headline">
              Welcome to Fika, <span>{name}</span>!
            </h1>
            <p className="onb-welcome-sub">
            We’re excited to have you as part of the creative community, and can’t wait for you to meet talented creatives near you over Fikas.
            </p>

            <div className="onb-welcome-footer">
              <button
                type="button"
                className="onb-nav-circle"
                onClick={handleNext}
                aria-label="Next"
              >
                →
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Two-column layout for all other steps ────────────────
  const isFirst = stepIndex === 1; // welcome is 0; first real step is 1

  const nextBlocked =
    (currentStepDef.key === "profilePhoto" && !profileImageFile) ||
    (currentStepDef.key === "favoriteWork" && !projectImageFile);

  return (
    <div className="ftueOverlay">
      <div className="ftueBackdrop" />
      <div className="ftueShell">
        <div className="onb-modal">

          {/* ── Left card ── */}
          <div className="onb-left">
            <div className="onb-left-body">
              <h1 className="onb-heading">{currentStepDef.heading}</h1>
              {currentStepDef.subtitle && (
                <p className="onb-subtitle">
                  {currentStepDef.subtitle.split("\n").map((line, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && <br />}
                      {line}
                    </React.Fragment>
                  ))}
                </p>
              )}
            </div>

            <div className="onb-step-foot">
              <div className="onb-step-label">Step {progressIndex + 1} of {progressSteps.length}</div>
              <div className="onb-progress-track">
                {progressSteps.map((_, i) => (
                  <div
                    key={i}
                    className={`onb-progress-seg${i <= progressIndex ? " onb-progress-seg--on" : ""}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* ── Right panel ── */}
          <div className="onb-right">
            <div className="onb-right-content">
              {renderRightContent()}
              {error && <p className="onb-error">{error}</p>}
            </div>

            <div className="onb-nav">
              {!isFirst && (
                <button
                  type="button"
                  className="onb-nav-circle"
                  onClick={prevStep}
                  disabled={saving}
                  aria-label="Back"
                >
                  ‹
                </button>
              )}
              {isLast ? (
                <button
                  type="button"
                  className="onb-nav-letsgo"
                  onClick={handleNext}
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Let's Go"}
                </button>
              ) : (
                <button
                  type="button"
                  className="onb-nav-circle"
                  onClick={handleNext}
                  disabled={saving || nextBlocked}
                  aria-label="Next"
                >
                  {saving ? "…" : "›"}
                </button>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
